// src/components/PhotoAnnotatorModal.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { PhotoAnnotatorToolbar } from "./PhotoAnnotatorToolbar";
import { PhotoAnnotatorOptions } from "./PhotoAnnotatorOptions";
import { PhotoAnnotatorFooter } from "./PhotoAnnotatorFooter";

/**
 * Modal that shows the image + drawing tools.
 *
 * Props:
 *  - isOpen: boolean
 *  - imageUrl: string
 *  - fileName: string
 *  - onKeep: ({ blob, dataUrl, fileName }) => void | Promise<void>
 *  - onRequestClose: () => void
 *  - emojis?: string[]
 */
export function PhotoAnnotatorModal({
  isOpen,
  imageUrl,
  fileName,
  onKeep,
  onRequestClose,
  emojis = ["😀", "😎", "😍", "🤔", "🔥", "👍", "🌴", "✈️", "📍"],
}) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const imageCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);

  const [tool, setTool] = useState("pencil");
  const [color, setColor] = useState("#ff0000");
  const [lineWidth, setLineWidth] = useState(4);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState(emojis[0] || "😀");

  const [isDrawing, setIsDrawing] = useState(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  // ---- Load image and size canvases based on container width ----
  useEffect(() => {
    if (!imageUrl || !isOpen) return;

    const img = new Image();
    img.onload = () => {
      const container = containerRef.current;

      // establishing container height and width is part of
      // the solution to scale an image to the canvas size
      container.style.height = "100vh";
      container.style.width = "100vw";

      const inner = innerRef.current;
      const imageCanvas = imageCanvasRef.current;
      const drawCanvas = drawCanvasRef.current;
      if (!container || !inner || !imageCanvas || !drawCanvas) return;

      let imgW = img.naturalWidth || img.width;
      let imgH = img.naturalHeight || img.height;

      // Use container width as our max width (minus a little padding)
      const padding = 16;
      const contW =
        container.clientWidth && container.clientWidth > padding
          ? container.clientWidth - padding
          : window.innerWidth - 32;

      // container height is part of image scaling problem
      const contH =
        container.clientHeight && container.clientHeight > padding
          ? container.clientHeight - padding
          : window.innerHeight - 32;

      // determining width or height having the lower
      // scaling factor is a part of scaling properly
      const scaleW = imgW > contW ? contW / imgW : 1;
      const scaleH = imgH > contH ? contH / imgH : 1;

      // determine which factor to be used in order to scale properly
      const scale = scaleH < scaleW ? scaleH : scaleW;

      const displayWidth = Math.max(1, Math.round(imgW * scale));
      const displayHeight = Math.max(1, Math.round(imgH * scale));

      // Internal resolution
      imageCanvas.width = displayWidth;
      imageCanvas.height = displayHeight;
      drawCanvas.width = displayWidth;
      drawCanvas.height = displayHeight;

      // Visual (CSS) size: match the scaled size
      imageCanvas.style.width = `${displayWidth}px`;
      imageCanvas.style.height = `${displayHeight}px`;
      drawCanvas.style.width = `${displayWidth}px`;
      drawCanvas.style.height = `${displayHeight}px`;

      // Make inner wrapper match exactly, so nothing is taller than it
      inner.style.width = `${displayWidth}px`;
      inner.style.height = `${displayHeight}px`;

      // Draw base image
      const ctx = imageCanvas.getContext("2d");
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

      // Clear overlay
      const drawCtx = drawCanvas.getContext("2d");
      drawCtx.clearRect(0, 0, displayWidth, displayHeight);
    };

    img.src = imageUrl;
  }, [imageUrl, isOpen]);

  // ---- Pointer helpers ----
  const getCanvasCoords = (event) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clientX =
      event.clientX ??
      event.touches?.[0]?.clientX ??
      event.changedTouches?.[0]?.clientX;
    const clientY =
      event.clientY ??
      event.touches?.[0]?.clientY ??
      event.changedTouches?.[0]?.clientY;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // ---- Drawing / Emoji ----
  const handlePointerDown = (e) => {
    if (tool === "emoji") {
      const { x, y } = getCanvasCoords(e);
      const canvas = drawCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const size = 32;
      ctx.font = `${size}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(selectedEmoji, x, y);
      return;
    }

    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);
    lastPointRef.current = { x, y };
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoords(e);
    const { x: lastX, y: lastY } = lastPointRef.current;

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = color;
    ctx.lineWidth = tool === "brush" ? lineWidth * 2 : lineWidth;

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();

    lastPointRef.current = { x, y };
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
  };

  const handlePointerLeave = () => {
    setIsDrawing(false);
  };

  const preventTouchScroll = (e) => {
    if (isDrawing || tool === "emoji") {
      e.preventDefault();
    }
  };

  const handleClear = () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // ---- Keep (compose image + drawing, return to parent) ----
  const handleKeep = useCallback(async () => {
    const imageCanvas = imageCanvasRef.current;
    const drawCanvas = drawCanvasRef.current;
    if (!imageCanvas || !drawCanvas) return;

    const w = imageCanvas.width;
    const h = imageCanvas.height;
    const offscreen = document.createElement("canvas");
    offscreen.width = w;
    offscreen.height = h;
    const ctx = offscreen.getContext("2d");

    ctx.drawImage(imageCanvas, 0, 0);
    ctx.drawImage(drawCanvas, 0, 0);

    offscreen.toBlob(
      async (blob) => {
        if (!blob) return;
        const dataUrl = offscreen.toDataURL("image/png");

        if (onKeep) {
          await onKeep({ blob, dataUrl, fileName });
        }
      },
      "image/png",
      0.92
    );
  }, [fileName, onKeep]);

  const handleCancel = () => {
    onRequestClose();
  };

  // ---- ESC closes ----
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="photo-annotator-backdrop" role="dialog" aria-modal="true">
      <div className="photo-annotator-modal">
        <PhotoAnnotatorToolbar
          tool={tool}
          selectedEmoji={selectedEmoji}
          emojis={emojis /* optional if you have this array */}
          onToolChange={setTool}
          onEmojiChange={setSelectedEmoji}
          onToggleOptions={() => setShowOptions((v) => !v)}
          onClear={handleClear}
        />

        {/* <PhotoAnnotatorToolbar
          tool={tool}
          selectedEmoji={selectedEmoji}
          onToolChange={setTool}
          onToggleOptions={() => setShowOptions((v) => !v)}
          onClear={handleClear}
        /> */}

        <PhotoAnnotatorOptions
          show={showOptions}
          color={color}
          lineWidth={lineWidth}
          emojis={emojis}
          selectedEmoji={selectedEmoji}
          onColorChange={setColor}
          onLineWidthChange={setLineWidth}
          onSelectedEmojiChange={setSelectedEmoji}
        />

        <div ref={containerRef} className="photo-annotator-canvas-container">
          <div ref={innerRef} className="photo-annotator-canvas-inner">
            <canvas
              ref={imageCanvasRef}
              className="photo-annotator-base-canvas"
            />
            <canvas
              ref={drawCanvasRef}
              className="photo-annotator-draw-canvas"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              onTouchStart={preventTouchScroll}
              onTouchMove={preventTouchScroll}
            />
          </div>
        </div>

        <PhotoAnnotatorFooter onCancel={handleCancel} onKeep={handleKeep} />

        <div className="photo-annotator-hint">
          This image will be kept on the form as a thumbnail. Your form save
          handler is responsible for storing it permanently.
        </div>
      </div>
    </div>
  );
}
